import { ChangeDetectionStrategy, Component, computed, inject, output } from '@angular/core';
import { TuiButton, TuiDialogService, TuiLoader, TuiNotification } from '@taiga-ui/core';
import { TUI_CONFIRM, TuiConfirmData, TuiToastService } from '@taiga-ui/kit';
import { switchMap } from 'rxjs';
import { RoleStore } from '@domains/role/role.store';
import { AuthStore } from '@domains/auth/auth.store';
import { PermissionManagementService } from '@core/services/permission-management.service';
import { IRole } from '@domains/role/role.model';

@Component({
  selector: 'app-settings-role-list',
  templateUrl: 'role-list.html',
  styleUrl: 'role-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TuiButton, TuiLoader, TuiNotification],
})
export class RoleListComponent {
  private readonly store = inject(RoleStore);
  private readonly authStore = inject(AuthStore);
  private readonly permissions = inject(PermissionManagementService);
  private readonly dialogs = inject(TuiDialogService);
  private readonly toasts = inject(TuiToastService);

  readonly add = output<void>();
  readonly edit = output<number>();
  readonly copy = output<number>();

  protected readonly roles = computed(() => this.store.roles());
  protected readonly isLoading = computed(
    () => this.store.isLoading() || this.permissions.isLoading(),
  );

  // A role cannot be deleted while it is the current user's own role
  protected readonly ownRoleId = computed(() => this.authStore.currentUser()?.role.id ?? null);

  protected readonly canList = computed(() => this.permissions.can('admin_rights_list'));
  protected readonly canCreate = computed(() => this.permissions.can('admin_rights_create'));
  protected readonly canEdit = computed(() => this.permissions.can('admin_rights_update'));
  protected readonly canCopy = computed(() => this.permissions.can('admin_rights_copy'));
  protected readonly canDelete = computed(() => this.permissions.can('admin_rights_delete'));

  protected onDelete(role: IRole): void {
    const data: TuiConfirmData = {
      content: `Do you want to delete the role "${role.name}"?`,
      yes: 'Delete',
      no: 'Cancel',
      appearance: 'destructive',
    };

    this.dialogs
      .open<boolean>(TUI_CONFIRM, { label: 'Delete role', size: 's', data })
      .pipe(switchMap((confirmed) => (confirmed ? this.store.deleteRole(role.id) : [])))
      .subscribe({
        next: () =>
          this.toasts.open('Role deleted successfully.', { appearance: 'positive' }).subscribe(),
        error: () =>
          this.toasts.open('Failed to delete role.', { appearance: 'negative' }).subscribe(),
      });
  }
}
